import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header/Header';
import ContactForm from '../../components/ContactForm/ContactForm';
import Footer from '../../components/Footer/Footer';
import SEO from '../../components/SEO/SEO';
import './Home.css';


const MODELS = [
  {
    key: 'mantaray420',
    name: 'Manta Ray 420',
    typeKey: 'centerConsole',
    length: '4.2 m',
    image: '/unimar-manta-ray-420.webp',
  },
  {
    key: 'stia580',
    name: 'STIA 580',
    typeKey: 'bowrider',
    length: '5.76 m',
    image: '/unimar-stia-580.webp',
  },
  {
    key: 'real500',
    name: 'Real 500',
    typeKey: 'deckBoat',
    length: '5 m',
    image: '/unimar-real-500.webp',
  },
];

function Home() {
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('reveal--visible'); observer.unobserve(e.target); } }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="home">
      <SEO
        title="Premium Fiberglass Boats — STIA 580, Manta Ray 420, Real 500"
        description="Unimar builds premium fiberglass boats in North Macedonia with over 40 years of experience. Explore the Manta Ray 420, Real 500, and STIA 580 — CE certified, 3-year warranty. Configure your boat online."
        url="/"
        image="/og-image.png"
      />
      <Header />

      {/* Hero Banner */}
      <section className="home-hero">
        <div className="home-hero__image-wrapper">
          <img
            src="/unimar-hero-banner.webp"
            alt="Unimar boat on water"
            className="home-hero__image"
          />
        </div>
        <div className="home-hero__overlay" />
        <div className="home-hero__content">
          <h1 className="home-hero__title">
            {t('home.hero.tagline')}
          </h1>
        </div>
      </section>

      {/* Unimar Mission Section */}
      <section className="home-mission">
        <div className="home-mission__inner reveal">
          <div className="home-mission__content">
            <img src="/logo-unimar.webp" alt="Unimar Logo" className="home-mission__logo" />
            <h2 className="home-mission__title">
              {t('home.hero.subtitle')}
            </h2>
            <p className="home-mission__text">
              {t('home.hero.body')}
            </p>
            <p className="home-mission__text">
              {t('home.hero.pitch1')}
            </p>
            <p className="home-mission__text">
              {t('home.hero.pitch2')}
            </p>
            <a href="/about" className="home-mission__cta" data-ga-click="cta-about-us">{t('home.hero.cta')}</a>
          </div>
          <div className="home-mission__image">
            <img src="/unimar-stia-580-design-sketch.webp" alt="Unimar Boat Design" />
          </div>
        </div>
      </section>

      {/* Explore Models Section */}
      <section className="home-models">
        <div className="home-models__inner">
          <h2 className="home-models__title">{t('home.models.title')}</h2>
          <div className="home-models__grid">
            {MODELS.map((model, i) => (
              <a key={model.name} href={`/models/${model.key}`} className="home-models__card reveal" style={{'--i': i}} data-ga-click={`model-card-${model.key}`}>
                <div className="home-models__card-image-wrapper">
                  <img
                    src={model.image}
                    alt={model.name}
                    className="home-models__card-image"
                  />
                </div>
                <h3 className="home-models__card-name">{model.name}</h3>
                <p className="home-models__card-type">{t(`home.models.${model.typeKey}`)}</p>
                <p className="home-models__card-length">{t('home.models.length')}: {model.length}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Not Sure Section */}
      <section className="home-cta">
        <div className="home-cta__inner">
          <h2 className="home-cta__title">{t('home.models.discoverCta').toUpperCase()}</h2>
          <p className="home-cta__text">{t('home.showcase.title')}</p>
          <div className="home-cta__buttons">
            <a href="/book-showcase" className="home-cta__btn home-cta__btn--primary" data-ga-click="cta-book-showcase-hero">{t('home.showcase.cta')}</a>
          </div>
        </div>
      </section>

      {/* Inspire Section */}
      <section className="home-inspire">
        <div className="home-inspire__inner">
          <h2 className="home-inspire__title reveal">
            {t('home.about.title')}
          </h2>
          <p className="home-inspire__text">{t('home.about.body1')}</p>
          <p className="home-inspire__text">{t('home.about.body2')}</p>
          <p className="home-inspire__text">{t('home.about.body3')}</p>
          <p className="home-inspire__text">{t('home.about.body4')}</p>
          <p className="home-inspire__text">{t('home.about.body5')}</p>
          <div className="home-inspire__images">
            <div className="home-inspire__image-wrapper reveal reveal--left">
              <img src="/unimar-balkans-adriatic-boat.webp" alt="Unimar boat on Lake Ohrid" className="home-inspire__image" loading="lazy" />
            </div>
            <div className="home-inspire__right-stack">
              <div className="home-inspire__image-wrapper reveal reveal--right">
                <img src="/unimar-balkans-lake-ohrid.webp" alt="Lake Ohrid church aerial" className="home-inspire__image" loading="lazy" />
              </div>
              <div className="home-inspire__image-wrapper reveal reveal--right">
                <img src="/unimar-boat-on-water.webp" alt="Unimar boat" className="home-inspire__image" loading="lazy" />
              </div>
            </div>
          </div>
          <a href="/models" className="home-inspire__cta" data-ga-click="cta-view-all-models">{t('home.about.cta')}</a>
        </div>
      </section>

      {/* Why Unimar Section */}
      <section className="home-why">
        <div className="home-why__inner">
          <h2 className="home-why__title">{t('home.difference.title')}</h2>
          <p className="home-why__text">{t('home.difference.body')}</p>
          <p className="home-why__text">{t('home.difference.body2')}</p>
          <p className="home-why__text">{t('home.difference.body3')}</p>
          <p className="home-why__text">{t('home.difference.body4')}</p>

          <div className="home-why__features">
            <div className="home-why__feature reveal" style={{'--i': 0}}>
              <div className="home-why__feature-icon">
                <img src="/icon-quality.webp" alt={t('home.difference.feature1Title')} loading="lazy" />
              </div>
              <h3 className="home-why__feature-title">{t('home.difference.feature1Title')}</h3>
              <p className="home-why__feature-text">{t('home.difference.feature1Body')}</p>
            </div>

            <div className="home-why__feature reveal" style={{'--i': 1}}>
              <div className="home-why__feature-icon">
                <img src="/icon-durability.webp" alt={t('home.difference.feature2Title')} loading="lazy" />
              </div>
              <h3 className="home-why__feature-title">{t('home.difference.feature2Title')}</h3>
              <p className="home-why__feature-text">{t('home.difference.feature2Body')}</p>
            </div>

            <div className="home-why__feature reveal" style={{'--i': 2}}>
              <div className="home-why__feature-icon">
                <img src="/icon-custom.webp" alt={t('home.difference.feature3Title')} loading="lazy" />
              </div>
              <h3 className="home-why__feature-title">{t('home.difference.feature3Title')}</h3>
              <p className="home-why__feature-text">{t('home.difference.feature3Body')}</p>
            </div>
          </div>

          <a href="/gallery" className="home-why__cta" data-ga-click="cta-view-gallery">{t('home.gallery.title')}</a>
        </div>
      </section>


      {/* Instagram Section */}
      <section className="home-insta">
        <div className="home-insta__inner">
          <h2 className="home-insta__title">{t('home.instagram.title')}</h2>
          <p className="home-insta__subtitle">{t('home.instagram.subtitle')}</p>
          <div className="home-insta__grid">
            <div className="home-insta__card">
              <img src="/unimar-social-2.webp" alt="UniMar Instagram" className="home-insta__img" loading="lazy" />
              <a href="https://www.instagram.com/p/DWEbF94Dtkz/?igsh=MWI4YzM2bjY0a2k0YQ==" target="_blank" rel="noopener noreferrer" className="home-insta__btn" data-ga-click="instagram-post-1">{t('home.instagram.viewPost')}</a>
            </div>
            <div className="home-insta__card">
              <img src="/unimar-social-1.webp" alt="UniMar Instagram" className="home-insta__img" loading="lazy" />
              <a href="https://www.instagram.com/p/DWgt3hFjslB/?igsh=MWt5eGZtOTN3enF3ZQ==" target="_blank" rel="noopener noreferrer" className="home-insta__btn" data-ga-click="instagram-post-2">{t('home.instagram.viewPost')}</a>
            </div>
            <div className="home-insta__card">
              <img src="/unimar-social-3.webp" alt="UniMar Instagram" className="home-insta__img" loading="lazy" />
              <a href="https://www.instagram.com/p/DWTkhwGDLaB/?igsh=MTR3Nm5jeDlhN3FleA==" target="_blank" rel="noopener noreferrer" className="home-insta__btn" data-ga-click="instagram-post-3">{t('home.instagram.viewPost')}</a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <ContactForm />

      <Footer />
    </div>
  );
}

export default Home;
